import { CalendarDays, MapPin, Users, Lock, EyeOff, Repeat, CheckCircle2, XCircle, HelpCircle } from "lucide-react";
import type { EventSummary } from "../api/types";

// Google Calendar's colorId palette (browser defaults). Used to render the side
// stripe for events with a colorId set.
const GOOGLE_EVENT_COLORS: Record<string, { name: string; hex: string }> = {
  "1": { name: "Lavender",  hex: "#7986CB" },
  "2": { name: "Sage",      hex: "#33B679" },
  "3": { name: "Grape",     hex: "#8E24AA" },
  "4": { name: "Flamingo",  hex: "#E67C73" },
  "5": { name: "Banana",    hex: "#F6BF26" },
  "6": { name: "Tangerine", hex: "#F4511E" },
  "7": { name: "Peacock",   hex: "#039BE5" },
  "8": { name: "Graphite",  hex: "#616161" },
  "9": { name: "Blueberry", hex: "#3F51B5" },
  "10": { name: "Basil",    hex: "#0B8043" },
  "11": { name: "Tomato",   hex: "#D50000" },
};

export default function EventList({ events }: { events: EventSummary[] }) {
  if (!events.length) {
    return (
      <div className="rounded-xl border border-slate-800 bg-slate-900 p-6 text-sm text-slate-400">
        No events in today&apos;s window.
      </div>
    );
  }
  return (
    <ul className="space-y-2">
      {events.map((e) => (
        <EventRow key={e.id} event={e} />
      ))}
    </ul>
  );
}

function EventRow({ event: e }: { event: EventSummary }) {
  const color = e.color_id ? GOOGLE_EVENT_COLORS[e.color_id] : null;
  const kind = categorize(e);

  return (
    <li
      className={`relative flex gap-3 overflow-hidden rounded-xl border bg-slate-900 p-3 ${
        e.is_matched ? "border-brand" : "border-slate-800"
      }`}
    >
      {/* Left color stripe: Google colorId if set, else a muted neutral. */}
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-1.5"
        style={{ backgroundColor: color?.hex ?? (e.is_matched ? "#FFAA00" : "#334155") }}
      />

      <div className="ml-2 flex-1 space-y-1.5">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className={`font-medium ${e.is_matched ? "text-brand" : "text-slate-100"}`}>
            {e.title || "(untitled)"}
          </span>
          <KindBadge kind={kind} />
          {e.is_matched ? <span className="rounded-full bg-brand/20 px-2 py-0.5 text-[10px] uppercase text-brand">matched</span> : null}
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-400">
          <span className="font-mono text-slate-300">
            {kind === "all-day" ? "all day" : `${formatTime(e.start)}–${formatTime(e.end)}`}
          </span>
          {e.calendar_name ? (
            <span className="flex items-center gap-1">
              <CalendarDays className="h-3 w-3" />
              {e.calendar_name}
              {e.is_primary_calendar ? <span className="text-[10px] text-slate-500">(primary)</span> : null}
            </span>
          ) : null}
          {e.attendee_count > 1 ? (
            <span className="flex items-center gap-1">
              <Users className="h-3 w-3" />
              {e.attendee_count}
              {e.is_organizer ? <span className="text-[10px] text-slate-500">· organizer</span> : null}
            </span>
          ) : null}
          {e.recurring ? (
            <span className="flex items-center gap-1">
              <Repeat className="h-3 w-3" />
              recurring
            </span>
          ) : null}
          <ResponseBadge response={e.my_response} />
          <VisibilityBadge visibility={e.visibility} transparency={e.transparency} />
          {color ? (
            <span className="flex items-center gap-1">
              <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: color.hex }} />
              {color.name}
            </span>
          ) : null}
        </div>
      </div>
    </li>
  );
}

type Kind = "all-day" | "location" | "focus" | "ooo" | "gmail" | "meeting";

function categorize(e: EventSummary): Kind {
  if (e.event_type === "workingLocation") return "location";
  if (e.event_type === "outOfOffice") return "ooo";
  if (e.event_type === "focusTime") return "focus";
  if (e.event_type === "fromGmail") return "gmail";
  if (e.all_day) return "all-day";
  return "meeting";
}

function KindBadge({ kind }: { kind: Kind }) {
  const meta: Record<Kind, { label: string; cls: string }> = {
    "all-day":  { label: "All-day",  cls: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30" },
    "location": { label: "Location", cls: "bg-teal-500/15 text-teal-300 border-teal-500/30" },
    "focus":    { label: "Focus",    cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
    "ooo":      { label: "OOO",      cls: "bg-rose-500/15 text-rose-300 border-rose-500/30" },
    "gmail":    { label: "Gmail",    cls: "bg-violet-500/15 text-violet-300 border-violet-500/30" },
    "meeting":  { label: "Meeting",  cls: "bg-slate-500/20 text-slate-300 border-slate-600/50" },
  };
  const m = meta[kind];
  const Icon = kind === "location" ? MapPin : kind === "all-day" ? CalendarDays : null;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide ${m.cls}`}>
      {Icon ? <Icon className="h-3 w-3" /> : null}
      {m.label}
    </span>
  );
}

function ResponseBadge({ response }: { response: string | null }) {
  if (!response || response === "accepted" || response === "needsAction") {
    if (response === "accepted") {
      return (
        <span className="flex items-center gap-1 text-emerald-300">
          <CheckCircle2 className="h-3 w-3" />
          accepted
        </span>
      );
    }
    return null;
  }
  if (response === "declined") {
    return (
      <span className="flex items-center gap-1 text-rose-300">
        <XCircle className="h-3 w-3" />
        declined
      </span>
    );
  }
  if (response === "tentative") {
    return (
      <span className="flex items-center gap-1 text-amber-300">
        <HelpCircle className="h-3 w-3" />
        tentative
      </span>
    );
  }
  return null;
}

function VisibilityBadge({ visibility, transparency }: { visibility: string | null; transparency: string | null }) {
  const items: React.ReactNode[] = [];
  if (visibility === "private" || visibility === "confidential") {
    items.push(
      <span key="priv" className="flex items-center gap-1 text-slate-300">
        <Lock className="h-3 w-3" />
        private
      </span>,
    );
  }
  if (transparency === "transparent") {
    items.push(
      <span key="free" className="flex items-center gap-1 text-slate-400">
        <EyeOff className="h-3 w-3" />
        free
      </span>,
    );
  }
  return <>{items}</>;
}

function formatTime(iso: string | null): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}
